import { useState } from "react";
import { Package } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SetImage — Componente robusto para mostrar imágenes de sets LEGO.
 *
 * TODAS las imágenes se almacenan en Supabase Storage (bucket set-images).
 * Este componente maneja automáticamente errores (raros, ya que Storage es fiable).
 *
 * Características:
 * - Lazy loading nativo
 * - Fallback a placeholder en caso de error
 * - Accesible (atributos ARIA)
 * - Sin dependencias externas de CDN
 *
 * @param src - URL de la imagen (desde Supabase Storage)
 * @param alt - Texto alternativo
 * @param className - Clases CSS adicionales para la imagen
 * @param fallbackClassName - Clases CSS para el contenedor del fallback
 */

interface SetImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallbackClassName?: string;
}

export function SetImage({ src, alt, className, fallbackClassName }: SetImageProps) {
  const [hasError, setHasError] = useState(false);

  // Normalizar URLs internas de desarrollo (ej. http://kong:8000/... -> SUPABASE_URL/...)
  const normalizedSrc = (() => {
    if (!src) return src;
    if (src.includes("kong:8000")) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "http://127.0.0.1:54331";
      return src.replace(/^http:\/\/kong:8000/, supabaseUrl);
    }
    return src;
  })();

  // Si no hay src en BD (nunca fue descargada) o la imagen falló a cargar
  if (!normalizedSrc || hasError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted/20",
          fallbackClassName || className
        )}
        role="img"
        aria-label={`Placeholder para ${alt}`}
      >
        <Package className="h-8 w-8 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <img
      src={normalizedSrc}
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
}
